import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Loader2, Upload } from 'lucide-react';
import { useContractDocuments } from '@/hooks/useContractDocuments';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenantId } from '@/hooks/useTenantId';

const formSchema = z.object({
  company_id: z.string().min(1, 'Company is required'),
  document_type: z.string().min(1, 'Contract type is required'),
  contract_parties: z.string().optional(),
  contract_value: z.string().optional(),
  contract_description: z.string().optional(),
  expiry_date: z.string().min(1, 'Expiration date is required'),
  auto_renewal: z.boolean().default(false),
});

type FormData = z.infer<typeof formSchema>;

interface ContractUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedCompanyId?: string;
}

const CONTRACT_TYPES = [
  'Master Service Agreement',
  'Subcontract Agreement',
  'Purchase Order',
  'Vendor Agreement',
  'Non-Disclosure Agreement',
  'Consulting Agreement',
  'License Agreement',
  'Lease Agreement',
  'Other',
];

export function ContractUploadDialog({ 
  open, 
  onOpenChange,
  preselectedCompanyId,
}: ContractUploadDialogProps) {
  const { tenantId } = useTenantId();
  const { uploadContract, isUploading } = useContractDocuments();
  const [file, setFile] = useState<File | null>(null);

  const { data: companies = [] } = useQuery({
    queryKey: ['companies-list', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, company_type')
        .eq('tenant_id', tenantId)
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId && open,
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_id: preselectedCompanyId || '',
      document_type: '',
      contract_parties: '',
      contract_value: '',
      contract_description: '',
      expiry_date: '',
      auto_renewal: false,
    },
  });

  // Update company_id when preselectedCompanyId changes or dialog opens
  useEffect(() => {
    if (open && preselectedCompanyId) {
      form.setValue('company_id', preselectedCompanyId);
    }
  }, [open, preselectedCompanyId, form]);

  const onSubmit = async (data: FormData) => {
    if (!file) {
      form.setError('root', { message: 'Please select a file to upload' });
      return;
    }

    try {
      await uploadContract({
        company_id: data.company_id,
        file,
        document_type: data.document_type,
        contract_parties: data.contract_parties,
        contract_value: data.contract_value ? parseFloat(data.contract_value) : undefined,
        contract_description: data.contract_description,
        expiry_date: data.expiry_date,
        auto_renewal: data.auto_renewal,
      });
      
      form.reset();
      setFile(null);
      onOpenChange(false);
    } catch (error) {
      // Error is handled by the hook
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Upload Contract</DialogTitle>
          <DialogDescription>
            Upload a new contract document for a contractor or client
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="company_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a company" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name} ({company.company_type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="document_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contract Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select contract type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CONTRACT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <FormLabel>Contract File</FormLabel>
              <Input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
              />
              {file && (
                <p className="text-sm text-muted-foreground">
                  Selected: {file.name}
                </p>
              )}
              {form.formState.errors.root && (
                <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
              )}
            </div>

            <FormField
              control={form.control}
              name="contract_parties"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contract Parties</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., ABC Corp and XYZ Inc" {...field} />
                  </FormControl>
                  <FormDescription>
                    Names of parties involved in this contract
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="contract_value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contract Value ($)</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="0.00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expiry_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiration Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="auto_renewal"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Auto-Renewal</FormLabel>
                    <FormDescription>
                      Contract automatically renews upon expiration
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contract_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description / Notes</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Add any relevant notes about this contract..."
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isUploading}>
                {isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Upload Contract
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
